import React from "react";
import type { PublicLandPriceResponse } from "@/features/publicLandPrice/types";
import {
  Box,
  Grid,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { getPrefectureNameById } from "@/utils/utilsAcquireBook";
import { LandPricePreviousPublicPrices } from "@/features/publicLandPrice/components/LandPricePreviousPublicPrices";

const renderBoolean = (value: boolean | null): string => {
  if (value === null) return "情報なし";
  return value ? "はい" : "いいえ";
};

const renderselectionYearFlag = (value: string | null): string => {
  if (value === "0") {
    return "選定対象年ではない";
  }
  if (value === "1") {
    return "選定対象年である";
  }
  return "情報なし";
};

const makeMappedPublicLandPrice = (
  landPrice: PublicLandPriceResponse
): Map<string, string | number | boolean | null> => {
  return new Map<string, string | number | boolean | null>([
    ["所在", landPrice.location],
    ["地番", landPrice.chiban],
    [
      "都道府県",
      `${getPrefectureNameById(Number(landPrice.prefectureId)) ?? ""}`,
    ],
    ["公示価格", `${landPrice.landPrice ?? "-"}円`],
    ["年度", `${landPrice.year ?? "-"}年`],
    ["住居表示", landPrice.jukyoHyoji],
    ["代表点緯度", landPrice.latitude],
    ["代表点経度", landPrice.longitude],
    ["標準地番号（行政区域）", landPrice.administrativeAreaCode],
    ["標準地番号（用途区分）", landPrice.areaUsePurpose],
    ["標準地番号（連番）", landPrice.branchNumber],
    ["対前年変動率", landPrice.yearOnYearChangeRate],

    ["地積", landPrice.landArea],
    ["利用現況大分類", landPrice.currentUsageCategory],
    ["地利用現況詳細", landPrice.usageDisplayDetail],
    ["利用区分", landPrice.usageCategory],
    ["建物構造", landPrice.buildingStructure],
    ["供給施設有無（ガス）", renderBoolean(landPrice.isGasSupply)],
    ["供給施設有無（水道）", renderBoolean(landPrice.isWaterSupply)],
    ["供給施設有無（下水道）", renderBoolean(landPrice.isSewageSupply)],
    ["形状", landPrice.shape],
    ["間口比率", `${landPrice.frontageRatio ?? "-"}%`],
    ["奥行比率", `${landPrice.depthRatio ?? "-"}%`],
    ["地上階層", `${landPrice.floors ?? "-"}階`],
    ["地下階層", `${landPrice.undergroundFloors ?? "-"}階`],
    ["前面道路状況（道路区分）", landPrice.frontRoadCondition],
    ["前面道路状況（方位）", landPrice.frontRoadDirection],
    ["前面道路状況（幅員）", landPrice.frontRoadWidth],

    ["前面道路状況（駅前状況）", landPrice.frontRoadStationStatus],
    ["前面道路状況（舗装状況）", landPrice.frontRoadPavementStatus],
    ["側道状況（接面道道状況）", landPrice.sideRoadCondition],
    ["側道状況（側道の方位）", landPrice.sideRoadDirection],
    ["周辺の土地利用の状況", landPrice.surroundingLandUseStatus],
    ["交通施設、距離（最寄り駅名）", landPrice.stationName],
    [
      "交通施設、距離（交通施設との近接状況）",
      landPrice.proximityToTransportFacility,
    ],
    ["交通施設、距離（最寄り駅迄の道路距離）", landPrice.distanceFromStation],
    [
      "法規制等（都市計画の用途地域）",
      landPrice.usageCategoryTypeForCityPlanning,
    ],
    ["法規制等（防火地域）", landPrice.fireProtectionArea],
    ["法規制等（都市計画区分", landPrice.cityPlanningCategory],
    ["法規制等（高度地区）", landPrice.highDistrict],
    ["法規制等（森林法）", landPrice.forestAct],
    ["前法規制等（自然公園法）", landPrice.naturalParkAct],
    ["法規制等（建蔽率）", landPrice.buildingCoverageRatio],

    ["法規制等（容積率）", landPrice.floorAreaRatio],
    ["法規制等（割増容積率）", renderBoolean(landPrice.isExtraFloorAreaRatio)],
    ["共通地点", renderBoolean(landPrice.isCommonPoint)],
    ["選定年次フラグ", renderselectionYearFlag(landPrice.selectionYearFlag)],
  ]);
};

// テーブルの１列目のみ色を塗り太字にする
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}:first-child`]: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.common.black,
    fontWeight: "bold",
    width: 262,
  },
}));

export const LandPriceDetailsTable: React.FC<{
  landPrice: PublicLandPriceResponse;
}> = (props) => {
  const { landPrice } = props;

  const mappedLandprice = makeMappedPublicLandPrice(landPrice);

  return (
    <Box m={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <LandPricePreviousPublicPrices landPrice={landPrice} />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Typography variant={"h5"} sx={{ mb: 1 }}>
            地価詳細情報
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="public land price">
              <TableBody>
                {[...mappedLandprice.entries()].map((item, index) => (
                  <TableRow key={index}>
                    <StyledTableCell component="th" scope="row">
                      {item[0]}
                    </StyledTableCell>
                    <StyledTableCell align="left">{item[1]}</StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};
